var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function (t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s)
                if (Object.prototype.hasOwnProperty.call(s, p))
                    t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Card, Col, DatePicker, Row, Select, Table, Typography } from 'antd';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, } from 'recharts';
import dayjs from 'dayjs';
import './ScreenAnalyticsPage.scss';
import { useGetScreenAnalyticsFlowsQuery, useGetScreenAnalyticsSummaryQuery, useGetSessionAnalyticsSummaryQuery, } from '@/services/analyticsApi';
var Title = Typography.Title, Text = Typography.Text;
var PERIOD_OPTIONS = [
    { value: 'день', label: 'День' },
    { value: 'неделя', label: 'Неделя' },
    { value: 'месяц', label: 'Месяц' },
    { value: 'год', label: 'Год' },
    { value: 'range', label: 'Промежуток' },
    { value: 'all', label: 'Все время' },
];
var PLATFORM_OPTIONS = [
    { value: '', label: 'Все платформы' },
    { value: 'ios', label: 'iOS' },
    { value: 'android', label: 'Android' },
];
var SCREEN_LABELS = {
    splash: 'Заставка',
    onboarding: 'Онбординг',
    login: 'Вход',
    register: 'Регистрация',
    city_select: 'Выбор города',
    tab_home: 'Главная',
    tab_map: 'Карта',
    tab_chats: 'Чаты (таб)',
    tab_events: 'События (таб)',
    tab_profile: 'Профиль (таб)',
    restaurant_list: 'Список заведений',
    restaurant_detail: 'Карточка заведения',
    restaurant_menu: 'Меню заведения',
    restaurant_map: 'Карта зала',
    restaurant_photos: 'Фото заведения',
    favorite_restaurants: 'Избранные заведения',
    chat_list: 'Список чатов',
    chat_detail: 'Чат',
    virtual_table: 'Виртуальный стол',
    recommendations_list: 'Рекомендации',
    recommendation_detail: 'Рекомендация',
    together_activities_list: 'Хочу вместе',
    together_activity_detail: 'Активность «вместе»',
    user_profile: 'Профиль пользователя',
    profile_edit: 'Редактирование профиля',
    profile_moderation_status: 'Статус модерации',
    events_list: 'Куда сходить',
    event_detail: 'Событие',
    promo_actions_list: 'Акции',
    promo_action_detail: 'Акция',
    coins_balance: 'Баланс монет',
    coins_topup: 'Пополнение монет',
    product_purchase: 'Покупка продукта',
    notifications: 'Уведомления',
    settings: 'Настройки',
    store_redirect: 'Переход в стор',
};
var formatScreen = function (name) { var _a; return (_a = SCREEN_LABELS[name]) !== null && _a !== void 0 ? _a : name; };
var formatDuration = function (ms) {
    if (!ms || ms <= 0)
        return '0 сек';
    var totalSec = Math.round(ms / 1000);
    if (totalSec < 60)
        return "".concat(totalSec, " \u0441\u0435\u043A");
    var min = Math.floor(totalSec / 60);
    var sec = totalSec % 60;
    if (min < 60)
        return sec > 0 ? "".concat(min, " \u043C\u0438\u043D ").concat(sec, " \u0441\u0435\u043A") : "".concat(min, " \u043C\u0438\u043D");
    var h = Math.floor(min / 60);
    var m = min % 60;
    return m > 0 ? "".concat(h, " \u0447 ").concat(m, " \u043C\u0438\u043D") : "".concat(h, " \u0447");
};
var formatInt = function (n) {
    return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(n || 0);
};
var ScreenAnalyticsPage = function () {
    var _a, _b, _c, _d, _e, _f, _g;
    var _h = useState('месяц'), period = _h[0], setPeriod = _h[1];
    var _j = useState(function () {
        return [
            dayjs().startOf('month'),
            dayjs().endOf('month'),
        ];
    }), range = _j[0], setRange = _j[1];
    var _k = useState(''), platform = _k[0], setPlatform = _k[1];
    var queryParams = useMemo(function () {
        var _a, _b;
        var base = { period: period };
        if (platform)
            base.platform = platform;
        if (period === 'range') {
            base.dateFrom = (_a = range === null || range === void 0 ? void 0 : range[0]) === null || _a === void 0 ? void 0 : _a.format('YYYY-MM-DD');
            base.dateTo = (_b = range === null || range === void 0 ? void 0 : range[1]) === null || _b === void 0 ? void 0 : _b.format('YYYY-MM-DD');
        }
        else if (period !== 'all') {
            base.date = dayjs().format('YYYY-MM-DD');
        }
        return base;
    }, [period, range, platform]);
    var _l = useGetScreenAnalyticsSummaryQuery(queryParams), summary = _l.data, loadingSummary = _l.isLoading;
    var _m = useGetScreenAnalyticsFlowsQuery(queryParams), flows = _m.data, loadingFlows = _m.isLoading;
    var _o = useGetSessionAnalyticsSummaryQuery(queryParams), sessions = _o.data, loadingSessions = _o.isLoading;
    var chartData = useMemo(function () {
        var _a;
        return ((_a = summary === null || summary === void 0 ? void 0 : summary.items) !== null && _a !== void 0 ? _a : []).slice(0, 12).map(function (item) {
            return ({
                name: formatScreen(item.screenName),
                minutes: Math.round((item.totalDurationMs / 60000) * 10) / 10,
            });
        });
    }, [summary]);
    var screenColumns = [
        {
            title: 'Экран',
            dataIndex: 'screenName',
            key: 'screenName',
            render: function (v) { return (_jsxs("div", { children: [_jsx("div", { children: formatScreen(v) }), _jsx(Text, __assign({ type: "secondary", style: { fontSize: 11 } }, { children: v }))] })); },
        },
        { title: 'Просмотры', dataIndex: 'viewCount', key: 'viewCount', render: formatInt },
        { title: 'Уник. пользователи', dataIndex: 'uniqueUsers', key: 'uniqueUsers', render: formatInt },
        {
            title: 'Суммарное время',
            dataIndex: 'totalDurationMs',
            key: 'totalDurationMs',
            render: formatDuration,
            sorter: function (a, b) { return a.totalDurationMs - b.totalDurationMs; },
            defaultSortOrder: 'descend',
        },
        {
            title: 'Среднее время',
            dataIndex: 'avgDurationMs',
            key: 'avgDurationMs',
            render: formatDuration,
        },
        {
            title: 'Доля времени',
            dataIndex: 'timeSharePercent',
            key: 'timeSharePercent',
            render: function (v) { return "".concat(v, "%"); },
        },
        {
            title: 'Выходы',
            dataIndex: 'exitCount',
            key: 'exitCount',
            render: formatInt,
        },
        {
            title: 'Exit rate',
            dataIndex: 'exitRate',
            key: 'exitRate',
            render: function (v) { return "".concat(v, "%"); },
        },
        {
            title: 'Быстрый уход (<5 сек)',
            dataIndex: 'shortVisitRate',
            key: 'shortVisitRate',
            render: function (v) { return "".concat(v, "%"); },
        },
    ];
    var flowColumns = [
        {
            title: 'Откуда',
            dataIndex: 'fromScreen',
            key: 'fromScreen',
            render: formatScreen,
        },
        {
            title: 'Куда',
            dataIndex: 'toScreen',
            key: 'toScreen',
            render: formatScreen,
        },
        { title: 'Переходов', dataIndex: 'count', key: 'count', render: formatInt },
    ];
    var exitColumns = [
        {
            title: 'Экран выхода',
            dataIndex: 'screenName',
            key: 'screenName',
            render: formatScreen,
        },
        { title: 'Кол-во выходов', dataIndex: 'exitCount', key: 'exitCount', render: formatInt },
    ];
    return (_jsxs("div", __assign({ className: "ScreenAnalyticsPage" }, { children: [_jsx("div", __assign({ className: "ScreenAnalyticsPage__titleBlock" }, { children: _jsx("span", __assign({ className: "ScreenAnalyticsPage__title" }, { children: "\u041C\u0435\u0442\u0440\u0438\u043A\u0438 \u0432\u0440\u0435\u043C\u0435\u043D\u0438 \u0432 \u043F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u0438\u0438" })) })), _jsxs("div", __assign({ className: "ScreenAnalyticsPage__content" }, { children: [_jsxs("div", __assign({ className: "ScreenAnalyticsPage__filters" }, { children: [_jsx(Select, { value: period, options: PERIOD_OPTIONS, style: { width: 170 }, onChange: function (v) { return setPeriod(v); } }), _jsx(Select, { value: platform, options: PLATFORM_OPTIONS, style: { width: 170 }, onChange: function (v) { return setPlatform(v); } }), _jsx(DatePicker.RangePicker, { value: range, onChange: function (v) {
                                    if (v && v[0] && v[1])
                                        setRange([v[0], v[1]]);
                                }, disabled: period !== 'range', format: "DD.MM.YYYY", allowClear: false })] })), _jsxs(Row, __assign({ gutter: [20, 20], className: "ScreenAnalyticsPage__kpiRow" }, { children: [_jsx(Col, __assign({ xs: 24, sm: 12, lg: 6 }, { children: _jsxs(Card, __assign({ className: "ScreenAnalyticsPage__kpiCard", loading: loadingSessions }, { children: [_jsx(Text, __assign({ type: "secondary" }, { children: "\u0421\u0435\u0441\u0441\u0438\u0439" })), _jsx(Title, __assign({ level: 4, className: "ScreenAnalyticsPage__kpiValue" }, { children: formatInt((_a = sessions === null || sessions === void 0 ? void 0 : sessions.sessionsCount) !== null && _a !== void 0 ? _a : 0) }))] })) })), _jsx(Col, __assign({ xs: 24, sm: 12, lg: 6 }, { children: _jsxs(Card, __assign({ className: "ScreenAnalyticsPage__kpiCard", loading: loadingSessions }, { children: [_jsx(Text, __assign({ type: "secondary" }, { children: "\u0423\u043D\u0438\u043A\u0430\u043B\u044C\u043D\u044B\u0445 \u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u0435\u043B\u0435\u0439" })), _jsx(Title, __assign({ level: 4, className: "ScreenAnalyticsPage__kpiValue" }, { children: formatInt((_b = sessions === null || sessions === void 0 ? void 0 : sessions.uniqueUsers) !== null && _b !== void 0 ? _b : 0) }))] })) })), _jsx(Col, __assign({ xs: 24, sm: 12, lg: 6 }, { children: _jsxs(Card, __assign({ className: "ScreenAnalyticsPage__kpiCard", loading: loadingSessions }, { children: [_jsx(Text, __assign({ type: "secondary" }, { children: "\u0421\u0440\u0435\u0434\u043D\u044F\u044F \u0434\u043B\u0438\u0442\u0435\u043B\u044C\u043D\u043E\u0441\u0442\u044C \u0441\u0435\u0441\u0441\u0438\u0438" })), _jsx(Title, __assign({ level: 4, className: "ScreenAnalyticsPage__kpiValue" }, { children: formatDuration((_c = sessions === null || sessions === void 0 ? void 0 : sessions.avgSessionDurationMs) !== null && _c !== void 0 ? _c : 0) }))] })) })), _jsx(Col, __assign({ xs: 24, sm: 12, lg: 6 }, { children: _jsxs(Card, __assign({ className: "ScreenAnalyticsPage__kpiCard", loading: loadingSummary }, { children: [_jsx(Text, __assign({ type: "secondary" }, { children: "\u0421\u0443\u043C\u043C\u0430\u0440\u043D\u043E\u0435 \u0432\u0440\u0435\u043C\u044F \u043D\u0430 \u044D\u043A\u0440\u0430\u043D\u0430\u0445" })), _jsx(Title, __assign({ level: 4, className: "ScreenAnalyticsPage__kpiValue" }, { children: formatDuration((_d = summary === null || summary === void 0 ? void 0 : summary.totalDurationMs) !== null && _d !== void 0 ? _d : 0) }))] })) }))] })), _jsx(Card, __assign({ className: "ScreenAnalyticsPage__sectionCard", title: "\u0422\u043E\u043F \u044D\u043A\u0440\u0430\u043D\u043E\u0432 \u043F\u043E \u0432\u0440\u0435\u043C\u0435\u043D\u0438 (\u043C\u0438\u043D\u0443\u0442\u044B)", loading: loadingSummary }, { children: chartData.length === 0 ? (_jsx(Text, __assign({ type: "secondary" }, { children: "\u041D\u0435\u0442 \u0434\u0430\u043D\u043D\u044B\u0445 \u0437\u0430 \u0432\u044B\u0431\u0440\u0430\u043D\u043D\u044B\u0439 \u043F\u0435\u0440\u0438\u043E\u0434" }))) : (_jsx(ResponsiveContainer, __assign({ width: "100%", height: 320 }, { children: _jsxs(BarChart, __assign({ data: chartData, margin: { top: 8, right: 16, left: 0, bottom: 48 } }, { children: [_jsx(CartesianGrid, { strokeDasharray: "3 3" }), _jsx(XAxis, { dataKey: "name", angle: -35, textAnchor: "end", interval: 0, height: 80, tick: { fontSize: 11 } }), _jsx(YAxis, { tick: { fontSize: 11 } }), _jsx(Tooltip, { formatter: function (v) { return ["".concat(v, " \u043C\u0438\u043D"), 'Время']; } }), _jsx(Bar, { dataKey: "minutes", fill: "#6C5CE7", radius: [4, 4, 0, 0] })] })) }))) })), _jsx(Card, __assign({ className: "ScreenAnalyticsPage__sectionCard", title: "\u0414\u0435\u0442\u0430\u043B\u0438\u0437\u0430\u0446\u0438\u044F \u043F\u043E \u044D\u043A\u0440\u0430\u043D\u0430\u043C" }, { children: _jsx(Table, { rowKey: "screenName", loading: loadingSummary, columns: screenColumns, dataSource: (_e = summary === null || summary === void 0 ? void 0 : summary.items) !== null && _e !== void 0 ? _e : [], pagination: { pageSize: 20, showSizeChanger: true }, scroll: { x: 1100 }, size: "middle" }) })), _jsxs(Row, __assign({ gutter: [20, 20], className: "ScreenAnalyticsPage__bottomRow" }, { children: [_jsx(Col, __assign({ xs: 24, lg: 12 }, { children: _jsx(Card, __assign({ className: "ScreenAnalyticsPage__sectionCard", title: "\u041F\u0435\u0440\u0435\u0445\u043E\u0434\u044B \u043C\u0435\u0436\u0434\u0443 \u044D\u043A\u0440\u0430\u043D\u0430\u043C\u0438 (\u0442\u043E\u043F-50)" }, { children: _jsx(Table, { rowKey: function (r) { return "".concat(r.fromScreen, "-").concat(r.toScreen); }, loading: loadingFlows, columns: flowColumns, dataSource: (_f = flows === null || flows === void 0 ? void 0 : flows.items) !== null && _f !== void 0 ? _f : [], pagination: { pageSize: 10 }, size: "middle" }) })) })), _jsx(Col, __assign({ xs: 24, lg: 12 }, { children: _jsx(Card, __assign({ className: "ScreenAnalyticsPage__sectionCard", title: "\u042D\u043A\u0440\u0430\u043D\u044B \u0432\u044B\u0445\u043E\u0434\u0430 \u0438\u0437 \u043F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u0438\u044F" }, { children: _jsx(Table, { rowKey: "screenName", loading: loadingSessions, columns: exitColumns, dataSource: (_g = sessions === null || sessions === void 0 ? void 0 : sessions.topExitScreens) !== null && _g !== void 0 ? _g : [], pagination: false, size: "middle" }) })) }))] }))] }))] })));
};
export default ScreenAnalyticsPage;
